/**
 * The Content Console's own sub-navigation over its six catalog types plus SEO.
 *
 * This used to be a second vertical `<aside>` sidebar, stacked below the app-level rail
 * (`shell/Sidebar.tsx`). Two vertical sidebars in one screen is two navs doing overlapping jobs —
 * confirmed as a real usability regression, not a matter of taste — so this is a single horizontal
 * row of tabs instead. The app-level rail answers "which section of the app", and this bar, now
 * clearly subordinate to it in the visual hierarchy, answers "which content type within Contenido".
 *
 * A `<nav>`, not `[role="tablist"]`: these are six real routes a visitor can bookmark, open in a new
 * tab, or reach by typing a URL, which is a link's contract rather than a tab panel's. Styling them to
 * look like tabs does not require the ARIA tab pattern's roving tabindex and arrow-key behavior — that
 * pattern is for switching panels of content that share one URL, which is not the case here.
 */
import { contentTypes } from '@apuestapr/contracts';
import { focusRing, icons } from '@apuestapr/ui';
import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { dashboardContentCatalog } from './catalog.ts';

const itemClassName = ({ isActive }: { readonly isActive: boolean }): string =>
  [
    'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 font-medium transition-colors duration-fast ease-standard',
    focusRing,
    isActive
      ? 'bg-secondary text-secondary-foreground'
      : 'text-foreground hover:bg-muted hover:text-muted-foreground',
  ].join(' ');

export const ContentLayout = (): ReactElement => (
  <div className="grid gap-6 py-6">
    <nav
      aria-label="Contenido"
      className="overflow-x-auto rounded-xl border border-border bg-card p-2 text-card-foreground"
    >
      <ul className="m-0 flex list-none gap-1 p-0">
        {contentTypes.map((type) => (
          <li key={type}>
            <NavLink className={itemClassName} end to={`/content/${type}`}>
              <icons.layers aria-hidden="true" />
              {dashboardContentCatalog[type].label}
            </NavLink>
          </li>
        ))}
        <li>
          <NavLink className={itemClassName} end to="/content/page-seo">
            <icons.fileText aria-hidden="true" />
            SEO
          </NavLink>
        </li>
      </ul>
    </nav>
    <div className="min-w-0">
      <Outlet />
    </div>
  </div>
);
